import {
	Component,
	DoCheck,
	ElementRef,
	EventEmitter,
	HostBinding,
	Input,
	OnDestroy,
	Optional,
	Output,
	Self
} from '@angular/core';
import {
	AbstractControl,
	ControlValueAccessor,
	FormBuilder,
	FormGroup,
	FormGroupDirective,
	NgControl,
	NgForm,
	Validators
} from '@angular/forms';
import {MatFormFieldControl} from '@angular/material/form-field';
import {Subject} from 'rxjs';
import {BooleanInput, coerceBooleanProperty} from '@angular/cdk/coercion';
import {FocusMonitor} from '@angular/cdk/a11y';

@Component({
	selector: 'creditsnap-yearmonth',
	templateUrl: './year-month-form-field.component.html',
	styleUrls: ['./year-month-form-field.component.scss'],
	providers: [{provide: MatFormFieldControl, useExisting: YearMonthFormFieldComponent}]
})
export class YearMonthFormFieldComponent implements OnDestroy, DoCheck, MatFormFieldControl<number>, ControlValueAccessor {
	@Input() userAriaDescribedBy: string;
	@Input('data-test-id') testId: string = 'year-month-input';
	@Input() groupLabel: string = '';

	parts: FormGroup;
	stateChanges: Subject<void> = new Subject<void>();
	id: string;
	// ngControl: NgControl | AbstractControlDirective;
	focused = false;
	errorState = false;
	touched: boolean;
	controlType?: string = 'year-month-value';
	autofilled?: boolean;

	private _placeholder: string;
	private _required = false;
	private _disabled = false;

	onChange = (_: number) => {
	};

	onTouched = () => {
	};

	@Output() valueChange: EventEmitter<number> = new EventEmitter<number>();

	@Input()
	get value(): number | null {
		const n = this.parts.value;
		if (n.years || n.months || (n.years === 0 && n.months === 0)) {
			return (Number(n.years) * 12) + Number(n.months);
		}
		return null;
	}

	set value(val: number | null) {
		//	val = val || 0;
		this.parts.setValue({years: Math.floor(val / 12), months: val % 12});
		this.stateChanges.next();
	}

	@Input()
	get placeholder() {
		return this._placeholder;
	}

	set placeholder(plh) {
		this._placeholder = plh;
		this.stateChanges.next();
	}

	get empty() {
		const value = this.parts.value;
		return !value.years && value.years !== 0 && !value.months && value.months !== 0;
	}

	@HostBinding('class.floating')
	get shouldLabelFloat() {
		return this.focused || !this.empty;
	}

	@Input()
	get required() {
		return this._required;
	}

	set required(req: boolean) {
		this._required = coerceBooleanProperty(req);
		this.stateChanges.next();
	}

	//
	// get errorState(): boolean {
	// 	return this.parts.invalid && this.touched;
	// }

	@Input()
	get disabled(): boolean {
		return this._disabled;
	}

	set disabled(value: BooleanInput) {
		this._disabled = coerceBooleanProperty(value);
		this._disabled ? this.parts.disable() : this.parts.enable();
		this.stateChanges.next();
	}

	constructor(fb: FormBuilder,
				private _focusMonitor: FocusMonitor,
				@Optional() @Self() public ngControl: NgControl,
				@Optional() private _parentForm: NgForm,
				@Optional() private _parentFormGroup: FormGroupDirective,
				private _elementRef: ElementRef
	) {
		this.parts = fb.group({
				years: [null, Validators.required],
				months: [null, [Validators.required, Validators.max(11), Validators.min(0)]]
			},
			{
				validator: this.checkYearMonthLength('years', 'months')
			});

		// Replace the provider from above with this.
		if (this.ngControl != null) {
			// Setting the value accessor directly (instead of using
			// the providers) to avoid running into a circular import.
			this.ngControl.valueAccessor = this;
		}

	}

    markAsTouched() {
        this.touched = true;
    }

	checkYearMonthLength(yearKey: string, monthKey: string) {
		return (group: FormGroup) => {
			const year = group.get(yearKey);
			const month = group.get(monthKey);
			const length = Number(year.value || 0) + Number(month.value || 0);
			// if (length <= 0 && month.touched) {
			if (length < 0 && this.ngControl.control) {
				this.ngControl.control.setErrors({lengthError: true});
				month.setErrors({lengthError: true});
				year.setErrors({lengthError: true});
				return;
			} else {
				if (this.ngControl.control?.errors?.lengthError) {
					const { lengthError, ...remaining } = this.ngControl.control.errors;
					this.ngControl.control.setErrors(Object.keys(remaining).length ? remaining : null);
				}
				month.setErrors(null);
				year.setErrors(null);
				return;
			}
		};
	}

	ngDoCheck() {
		if (this.ngControl) {
			this.updateErrorState();
		}
	}

	writeValue(val: number): void {
		if (val || val === 0) {
			this.value = val;
			this.onChange(val);
		}
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState?(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	setDescribedByIds(ids: string[]): void {
		const controlElement = this._elementRef.nativeElement
			.querySelector('.duration-input-container')!;
		controlElement.setAttribute('aria-describedby', ids.join(' '));
	}

	onContainerClick(event: MouseEvent): void {
		if ((event.target as Element).tagName.toLowerCase() != 'input') {
			this._elementRef.nativeElement.querySelector('input').focus();
		}
	}

	private updateErrorState() {
        const parent = this._parentForm || this._parentFormGroup;

		const oldState = this.errorState;
		const newState = (this.ngControl?.invalid || this.parts.invalid) && (this.touched || parent.submitted || this.ngControl?.control?.touched);
		if (oldState !== newState) {
			this.errorState = newState;
			this.stateChanges.next();
		}
	}

	onFocusIn(event: FocusEvent) {
		if (!this.focused) {
			this.focused = true;
			this.stateChanges.next();
		}
	}

	onFocusOut(event: FocusEvent) {
		if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
			this.touched = true;
			this.focused = false;
			this.onTouched();
			this.stateChanges.next();
		}
	}

	_handleInput(control: AbstractControl): void {
		//	this.autoFocusNext(control, nextElement);
		this.writeValue(this.value);
		this.valueChange.emit(this.value);
	}

	autoFocusNext(control: AbstractControl, nextElement?: HTMLInputElement): void {
		if (!control.errors && nextElement) {
			this._focusMonitor.focusVia(nextElement, 'program');
		}
	}

	autoFocusPrev(control: AbstractControl, prevElement: HTMLInputElement): void {
		if (control.value?.length < 1) {
			this._focusMonitor.focusVia(prevElement, 'program');
		}
	}

	ngOnDestroy() {
		this.stateChanges.complete();
		this._focusMonitor.stopMonitoring(this._elementRef);
	}

}
